import React, { Fragment, ReactElement } from 'react';

import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-components';
import { Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { ModalService } from '@philips-emr/tws-core/lib/services';
import { isEmptyOrBlankString, isFunction } from '@philips-emr/tws-utils';

import { FragmentNames } from '../../codegen';
import { Report, ReportService } from '../../services';
import BaseController from '../base/base-controller';

export interface Mensalidade {
  iePermiteSegundaVia: string,
  ieVisualizaNotaFiscal: string,
  ieNegociado: string,
  dsLinkNf: string,
  dsSituacao: string,
  nrSeqMensalidade: number;
  situacaoSuperscription: MensalidadeSituacaoSuperscription,
  nrTitulo: number,
  ieSituacao: string,
}

enum MensalidadeSituacaoSuperscription {
  VENCIDO = 'V',
  NEGOCIADO = 'N',
  ABERTO = '1',
  LIQUIDADO = '2',
  CANCELADO = '3',
  LIQUIDADO_CHEQUE = '4',
  TRIBUT_TRANSFERIDO = '5',
  LIQUIDADO_PERDA = '6',
}

const OptionMensalidade = {
  SEGUNDA_VIA: 'segunda-via',
  DETALHAMENTO: 'detalhamento',
  NOTA_FISCAL: 'nota-fiscal',
  OBSERVACOES: 'observacoes',
};

export default class BaseMensalidadeController extends BaseController {

  private exibeDetalhamento: boolean;
  private exibeSegundaVia: boolean;
  private recordDetalhamento: Report[];
  private recordSegundaVia: Report[];

  protected onMountFetchMensalidades(): void {
    void this.exibirDetalhamento().then((resp: boolean) => this.exibeDetalhamento = resp);
    void this.exibirSegundaVia().then((resp: boolean) => this.exibeSegundaVia = resp);
  }

  protected onDynamicContentRender({ data, render }: RenderEvent): void {
    const { situacaoSuperscription } = data as Mensalidade;

    render((): ReactElement => getMensalidadeStamp(situacaoSuperscription));
  }

  protected onCreateOption({ data, key, shouldRender }: HpmsCustomCardMenuRenderEvent<Mensalidade>): void {
    const {
      dsLinkNf,
      iePermiteSegundaVia,
      ieVisualizaNotaFiscal,
      ieSituacao,
    } = data;

    const show = {
      [OptionMensalidade.SEGUNDA_VIA]: this.exibeSegundaVia && ieSituacao === MensalidadeSituacaoSuperscription.ABERTO && iePermiteSegundaVia === 'S',
      [OptionMensalidade.DETALHAMENTO]: this.exibeDetalhamento,
      [OptionMensalidade.NOTA_FISCAL]: ieVisualizaNotaFiscal === 'S' && !isEmptyOrBlankString(dsLinkNf),
      [OptionMensalidade.OBSERVACOES]: true,
    };

    shouldRender(show[key]);
  }

  protected onOptionClick({ data, key }: HpmsCustomCardEvent<Mensalidade>): void {
    const { dsLinkNf, nrSeqMensalidade, nrTitulo } = data;

    const action = {
      [OptionMensalidade.SEGUNDA_VIA]: () => this.imprimirSegundaVia(nrTitulo),
      [OptionMensalidade.DETALHAMENTO]: () => this.imprimirDetalhamento(nrSeqMensalidade),
      [OptionMensalidade.NOTA_FISCAL]: () => this.notaFiscal(dsLinkNf),
      [OptionMensalidade.OBSERVACOES]: () => this.observacoes(nrSeqMensalidade),
    };

    if (isFunction(action[key])) {
      action[key]();
    }
  }

  private notaFiscal = (dsLinkNf: string): void => {
    if (!dsLinkNf.match(/^https?:\/\//i)) {
      dsLinkNf = 'http://' + dsLinkNf;
    }
    window.open(dsLinkNf, '_blank');
  };

  private observacoes = (nrSeqMensalidade: number): void => {
    const { nrSeqBeneficiario } = this.context.getBulkData();

    ModalService.openDefault({
      title: 'beneficiary.obs-mensalidade',
      fragment: FragmentNames.MODAL_OBSERVACOES_MENSALIDADES,
      contextData: { nrSeqBeneficiario, nrSeqMensalidade },
    });
  };

  private imprimirSegundaVia(nrSequencia: number): void {
    const { nrSeqBeneficiario } = this.context.getBulkData();

    ReportService.open({
      cdGrupo: '6',
      nrSeqBeneficiario: nrSeqBeneficiario as number,
      parametrosRelatorio: { NR_TITULO: nrSequencia },
      record: this.recordSegundaVia,
    });
  }

  private imprimirDetalhamento(nrSequencia: number): void {
    const { nrSeqBeneficiario } = this.context.getBulkData();

    ReportService.open({
      cdGrupo: '10',
      nrSeqBeneficiario: nrSeqBeneficiario as number,
      parametrosRelatorio: { NR_SEQ_MENSALIDADE: nrSequencia },
      record: this.recordDetalhamento,
    });
  }

  private exibirSegundaVia(): Promise<boolean> {
    const { nrSeqBeneficiario } = this.context.getBulkData();

    return ReportService.print({
      cdGrupo: '6',
      nrSeqBeneficiario: nrSeqBeneficiario as number,
    })
      .then(({ record }) => {
        this.recordSegundaVia = record;
        return true;
      })
      .catch(() => false);
  }

  private exibirDetalhamento(): Promise<boolean> {
    const { nrSeqBeneficiario } = this.context.getBulkData();

    return ReportService.print({
      cdGrupo: '10',
      nrSeqBeneficiario: nrSeqBeneficiario as number,
    })
      .then(({ record }) => {
        this.recordDetalhamento = record;
        return true;
      })
      .catch(() => false);
  }

}

function getMensalidadeStamp(situacaoSuperscription: MensalidadeSituacaoSuperscription): ReactElement {
  if ([
    MensalidadeSituacaoSuperscription.CANCELADO,
    MensalidadeSituacaoSuperscription.TRIBUT_TRANSFERIDO,
  ].includes(situacaoSuperscription)) {
    return <Fragment />;
  }

  return (
    <Stamp
      color={getMensalidadeIconColor(situacaoSuperscription)}
      icon={getMensalidadeIconType(situacaoSuperscription)}
      label={getMensalidadeStampLabel(situacaoSuperscription)}
    />
  );
}

function getMensalidadeStampLabel(situacaoSuperscription: MensalidadeSituacaoSuperscription): string {
  const labels: Record<string, string> = {
    [MensalidadeSituacaoSuperscription.NEGOCIADO]: 'beneficiary.negociado',
    [MensalidadeSituacaoSuperscription.VENCIDO]: 'beneficiary.vencido',
  };

  if (situacaoSuperscription in labels) {
    return labels[situacaoSuperscription];
  }

  return `@beneficiary.situacao-titulo/${situacaoSuperscription}`;

}

export function getMensalidadeIconColor(situacaoSuperscription: MensalidadeSituacaoSuperscription): Color {
  const color = {
    [MensalidadeSituacaoSuperscription.VENCIDO]: Color.SAFETY_RED,
    [MensalidadeSituacaoSuperscription.NEGOCIADO]: Color.SAFETY_NEUTRAL,
    [MensalidadeSituacaoSuperscription.ABERTO]: Color.SAFETY_ORANGE,
    [MensalidadeSituacaoSuperscription.LIQUIDADO]: Color.SAFETY_GREEN,
    [MensalidadeSituacaoSuperscription.CANCELADO]: Color.SAFETY_NEUTRAL,
    [MensalidadeSituacaoSuperscription.LIQUIDADO_CHEQUE]: Color.SAFETY_GREEN,
    [MensalidadeSituacaoSuperscription.TRIBUT_TRANSFERIDO]: Color.SAFETY_NEUTRAL,
    [MensalidadeSituacaoSuperscription.LIQUIDADO_PERDA]: Color.SAFETY_GREEN,
  };

  return color[situacaoSuperscription];
}

function getMensalidadeIconType(situacaoSuperscription: MensalidadeSituacaoSuperscription): StampIcon {
  const icon = {
    [MensalidadeSituacaoSuperscription.VENCIDO]: StampIcon.CANCELED,
    [MensalidadeSituacaoSuperscription.NEGOCIADO]: StampIcon.NONE,
    [MensalidadeSituacaoSuperscription.ABERTO]: StampIcon.WARNING,
    [MensalidadeSituacaoSuperscription.LIQUIDADO]: StampIcon.CHECK,
    [MensalidadeSituacaoSuperscription.CANCELADO]: StampIcon.NONE,
    [MensalidadeSituacaoSuperscription.LIQUIDADO_CHEQUE]: StampIcon.CHECK,
    [MensalidadeSituacaoSuperscription.TRIBUT_TRANSFERIDO]: StampIcon.NONE,
    [MensalidadeSituacaoSuperscription.LIQUIDADO_PERDA]: StampIcon.CHECK,
  };

  return icon[situacaoSuperscription];
}
